import axios from 'axios';
import React, { useState, useEffect } from 'react';
import {
  Button,
  Col,
  Grid,
  Row,
  Typography,
  Card,
  Form,
  Input,
  Select,
  Image,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  useLocation,
  useNavigate,
  useParams,
  Link,
} from 'react-router-dom';

import Layout from '../../../layouts/User';
import styles from '../index.module.scss';
import {
  validatePhoneNumber,
  validateNumberOnly,
} from '../../../functions';
import {
  getPostalCodes,
  getCities,
  getSubCities,
  getStates,
} from '../../../services/api';
import { ORDER_SERVICE_CATEGORY } from '../../../reducers/actions/orderService';

export type Props = {};

interface Technician {
  name: string;
  phone: string;
  address: string;
  province: string;
  district: string;
  subDistrict: string;
  postCode: string;
}

interface ServiceJob {
  _id: string;
  user: string;
  serviceCategory: string;
  topic: string;
  description: string;
  technician: Technician;
  services: { [key: string]: [] };
  idCard: string;
  status: string;
  remark: string[];
}

interface LocationState {
  category: string;
}

const ServiceJob: React.FC<Props> = (props) => {
  const { id } = useParams();
  const { useBreakpoint } = Grid;
  const location = useLocation();
  const [form] = Form.useForm();
  const state = location.state as LocationState;
  const { category } = state;

  const breakpoints = useBreakpoint();

  const [blockButton, setBlockbutton] = useState(false);
  const [service, setService] = useState<ServiceJob>({
    _id: '',
    user: '',
    serviceCategory: '',
    topic: '',
    description: '',
    technician: {
      name: '',
      phone: '',
      address: '',
      province: '',
      district: '',
      subDistrict: '',
      postCode: '',
    },
    services: {},
    idCard: '',
    status: '',
    remark: [],
  });
  const [loading, setLoading] = useState(false);
  const [postalCodes, setPostalCodes] = useState<any>([]);
  const [subCities, setSubCities] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [states, setStates] = useState<any>([]);

  const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  };
  const query = useQuery();

  const getService = async () => {
    const res = await axios.get(`/applyJobs/${id}`);

    const { data } = res;
    loadSubCities(data.applyJob.technician.postCode);
    loadCities(data.applyJob.technician.subDistrict);
    loadStates(data.applyJob.technician.district);
    setService(data.applyJob);
    form.setFieldsValue(data.applyJob);
  };

  useEffect(() => {
    let isBlock =
      breakpoints.xs ||
      ((breakpoints.sm || breakpoints.md) && !breakpoints.lg) ||
      false;
    setBlockbutton(isBlock);
  }, [breakpoints]);

  //#region [load master address]
  const loadSubCities = async (value: any) => {
    setSubCities(await getSubCities(value));
  };

  const loadCities = async (value: any) => {
    setCities(await getCities(value));
  };

  const loadStates = async (value: any) => {
    setStates(await getStates(value));
  };
  //#endregion

  const onPostalCodeChange = async (value: any) => {
    await loadSubCities(value);
    form.setFieldsValue({
      province: null,
      district: null,
      subDistrict: null,
    });
    return;
  };

  const onSubCityChange = async (value: any = null) => {
    await loadCities(value);
    return;
  };

  const onCityChange = async (value: any = null) => {
    await loadStates(value);
    return;
  };

  const initialPostalCode = async () => {
    setPostalCodes(await getPostalCodes());
  };

  useEffect(() => {
    getService();
    initialPostalCode();
  }, []);

  return (
    <>
      <Layout>
        <div className={styles.headerPath}>
          {' '}
          <Link
            to={`/services/jobs?category=${category}`}
            state={{ category }}
            key={category}
          >
            {'< '}โปรไฟล์ช่าง
          </Link>
        </div>
        <div className={styles.header}>ข้อมูลและรายละเอียดช่าง</div>
        <Form form={form} layout="vertical">
          <Card bordered={false} style={{ width: '100%' }}>
            <Row justify={'space-between'} gutter={[32, 32]}>
              <Col>
                <div className={styles.header}>ข้อมูลช่าง</div>
              </Col>
              <Col>
                <Link
                  to={`/services/contact/${id}?province=${query.get(
                    'province'
                  )}`}
                  state={{ category }}
                  key={id}
                >
                  <Button
                    type="primary"
                    size={'large'}
                    style={{ background: '#0063F2' }}
                  >
                    ติดต่อช่าง
                  </Button>
                </Link>
              </Col>
            </Row>
            <Form.Item
              name={['technician', 'name']}
              label="ชื่อผู้ติดต่อ"
            >
              <Input readOnly placeholder="ชื่อผู้ติดต่อ" />
            </Form.Item>

            <Form.Item
              name={['technician', 'phone']}
              label="เบอร์โทรศัพท์"
            >
              <Input
                readOnly
                placeholder="เบอร์โทรศัพท์"
                onKeyPress={(event) => validateNumberOnly(event)}
                maxLength={10}
              />
            </Form.Item>

            <Form.Item
              name={['technician', 'address']}
              label="ที่อยู่"
            >
              <Input placeholder="ที่อยู่" readOnly />
            </Form.Item>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                {' '}
                <Form.Item
                  name={['technician', 'postCode']}
                  label="รหัสไปรษณีย์"
                  rules={[
                    {
                      required: true,
                      message: 'กรุณากรอกข้อมูล',
                    },
                  ]}
                >
                  <Select
                    showSearch
                    onChange={onPostalCodeChange}
                    options={postalCodes}
                    placeholder="รหัสไปรษณีย์"
                  ></Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['technician', 'subDistrict']}
                  label="แขวง / ตำบล"
                  rules={[
                    {
                      required: true,
                      message: 'กรุณากรอกข้อมูล',
                    },
                  ]}
                >
                  <Select
                    showSearch
                    filterOption={(input, option: any) =>
                      option.label.indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={onSubCityChange}
                    options={subCities}
                    placeholder="แขวง / ตำบล"
                  ></Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['technician', 'district']}
                  label="เขต / อำเภอ"
                  rules={[
                    {
                      required: true,
                      message: 'กรุณากรอกข้อมูล',
                    },
                  ]}
                >
                  <Select
                    showSearch
                    filterOption={(input, option: any) =>
                      option.label.indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={onCityChange}
                    options={cities}
                    placeholder="เขต / อำเภอ"
                  ></Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={['technician', 'province']}
                  label="จังหวัด"
                >
                  <Select
                    showSearch
                    filterOption={(input, option: any) =>
                      option.label.indexOf(input.toLowerCase()) >= 0
                    }
                    options={states}
                    placeholder="จังหวัด"
                  ></Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="description"
              label="รายละเอียดเพิ่มเติม (ถ้ามี)"
            >
              <Input.TextArea
                readOnly
                placeholder="รายละเอียดเพิ่มเติม"
              />
            </Form.Item>
          </Card>
          <br />
          <Card bordered={false} style={{ width: '100%' }}>
            <Row gutter={[32, 32]}>
              <Col>
                <div className={styles.header}>รายละเอียดช่าง</div>
              </Col>
            </Row>
            <Form.Item name="topic" label="ชื่อประกาศ">
              <Input readOnly placeholder="ชื่อผู้ติดต่อ" />
            </Form.Item>

            <Form.Item name="description" label="รายละเอียดงาน">
              <Input.TextArea
                readOnly
                placeholder="รายละเอียดเพิ่มเติม"
              />
            </Form.Item>

            <Row gutter={[32, 32]}>
              <Col>
                <div className={styles.header}>ผลงานของนายช่าง</div>
                <div className={styles.jobGridImages}>
                  {service?.services &&
                    Object.keys(service?.services).map(
                      (item: string) => {
                        return (
                          <div key={`img-${item}`}>
                            <div>{ORDER_SERVICE_CATEGORY[item]}</div>
                            <Image.PreviewGroup key={`imgs-${item}`}>
                              {service?.services[item].map(
                                (imgUrl: string, i: number) => {
                                  return (
                                    <Image
                                      alt={`j-img-${i}`}
                                      className={
                                        styles.jobDetailImage
                                      }
                                      src={imgUrl}
                                      key={`j-img-${i}`}
                                    />
                                  );
                                }
                              )}
                            </Image.PreviewGroup>
                          </div>
                        );
                      }
                    )}
                </div>
              </Col>
            </Row>
          </Card>
        </Form>
      </Layout>
    </>
  );
};

export default ServiceJob;
